import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary component that catches React errors and can trigger a page refresh
 * when React state corruption is detected (e.g., dispatcher is null, invalid hook calls)
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a React hook corruption error
    const isReactHookError = 
      error.message?.includes('Invalid hook call') ||
      error.message?.includes('dispatcher is null') ||
      error.message?.includes("can't access property") ||
      error.stack?.includes('useContext') ||
      error.stack?.includes('useTranslation');

    if (isReactHookError) {
      // Call the onError callback if provided
      if (this.props.onError) {
        this.props.onError(error, errorInfo);
      }
      
      // Auto-refresh the page after a short delay to allow error to be logged
      const refreshKey = '__react_error_refresh_attempted';
      if (!sessionStorage.getItem(refreshKey)) {
        sessionStorage.setItem(refreshKey, 'true');
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // If it's a React hook error, show a brief message before refresh
      const isReactHookError = 
        this.state.error?.message?.includes('Invalid hook call') ||
        this.state.error?.message?.includes('dispatcher is null');

      if (isReactHookError) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-bg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <div className="text-text">Refreshing page...</div>
            </div>
          </div>
        );
      }

      // For other errors, show a fallback UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg">
          <div className="text-center max-w-md px-4">
            <h1 className="text-2xl font-bold text-text mb-2">Something went wrong</h1>
            <p className="text-text-dim mb-4">
              An unexpected error occurred. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/80"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
